import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Check, X, ArrowRight, CalendarDays, Info, Maximize2, Clock, Package, AlertTriangle, Building2 } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useGroups } from '../store/groups';
import { useInventory, InventoryItem } from '../store/inventory';
import { Avatar, BottomSheet, SecondaryBtn, Badge, Spinner, useConfirm, ImageLightbox, ImageTile, ImageTileBadge, DueSoonBanner } from '../components/UI';
import { CategoryIcon } from '../lib/categoryIcons';
import { EditItemSheet } from '../components/EditItemSheet';
import { LoanOutSheet } from '../components/LoanOutSheet';

type DetailSection = 'mine' | 'loans' | 'lentout';
type Tab = 'mine' | 'loans' | 'lentout';

export const MyToolsPage: React.FC<{ addOpen: boolean; onCloseAdd: () => void }> = ({ addOpen, onCloseAdd }) => {
  const { user } = useAuth();
  const { groups } = useGroups();
  const {
    items, myLoans, myLentOut,
    fetchItems, fetchMyLoans, fetchMyLentOut,
    deleteItem,
    returnItem, approveLoanItem, rejectLoanItem, cancelLoanItem,
  } = useInventory();
  const { confirm, dialog } = useConfirm();

  const [loanItemId, setLoanItemId] = useState<string | null>(null);
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<InventoryItem | null>(null);
  const [detailSection, setDetailSection] = useState<DetailSection | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('mine');
  const [approveMessage, setApproveMessage] = useState('');
  const [returnMessage, setReturnMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    fetchItems(user.id);
    fetchMyLoans(user.id);
    fetchMyLentOut(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Poll every 4 s when pending loans exist so borrower sees approval without Realtime
  const hasPendingLoans = myLoans.some(l => l.status === 'pending');
  useEffect(() => {
    if (!user || !hasPendingLoans) return;
    const id = setInterval(() => fetchMyLoans(user.id), 4000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasPendingLoans]);

  const mine = items.filter(i => i.user_id === user?.id && i.loanable);

  const getGroupNames = (item: any): string[] => {
    const gids: string[] = item.groups || [];
    return gids.map((gid: string) => groups.find(g => g.id === gid)?.name).filter(Boolean) as string[];
  };

  const isOverdue = (due_date: string | null, status: string) =>
    !!due_date && new Date(due_date) < new Date() && status === 'loaned';

  const handleDelete = async (id: string) => {
    if (!await confirm('Slette dette fra utlånslisten?', { confirmLabel: 'Slett' })) return;
    await deleteItem(id);
  };

  const openDetail = (item: InventoryItem, section: DetailSection) => {
    setDetailItem(item);
    setDetailSection(section);
  };

  const closeDetail = () => {
    setDetailItem(null);
    setDetailSection(null);
  };

  if (!user) return <Spinner />;

  return (
    <div>
      <DueSoonBanner items={myLoans} />

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any, paddingBottom: 2 }}>
        {([
          { key: 'mine' as Tab,    label: 'Mine ting',      count: mine.length },
          { key: 'loans' as Tab,   label: 'Jeg låner',      count: myLoans.length },
          { key: 'lentout' as Tab, label: 'Utlånt av meg',  count: myLentOut.length },
        ]).map(t => {
          const active = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{
                flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                padding: '8px 6px', borderRadius: 20, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
                background: active ? 'var(--green)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--text2)',
              }}>
              {t.label} ({t.count})
            </button>
          );
        })}
      </div>

      {/* ── Mine lånbare ting ── */}
      {activeTab === 'mine' && <>
      {mine.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Info size={32} color="var(--text3)" strokeWidth={1.8} />
          </div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Ingen lånbare ting enda</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Gå til «Inventar» og aktiver «Kan lånes ut» på en ting</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {mine.map(item => {
          const loanedTo = item.loaned_to;
          const overdue = isOverdue(item.due_date, item.status);
          const isPending = item.status === 'pending';
          const dueDateLabel = item.due_date ? new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : null;

          let statusColor = 'var(--green)';
          let statusBg = 'var(--green-light)';
          let StatusIcon = Check;
          let statusLabel = 'Ledig';
          if (isPending) { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Clock; statusLabel = 'Venter'; }
          else if (item.status === 'loaned' && overdue) { statusColor = 'var(--red)'; statusBg = 'var(--red-light)'; StatusIcon = AlertTriangle; statusLabel = dueDateLabel ? `Forfalt ${dueDateLabel}` : 'Forfalt'; }
          else if (item.status === 'loaned') { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Package; statusLabel = dueDateLabel ? `Utlånt til ${dueDateLabel}` : 'Utlånt'; }

          return (
            <ImageTile
              key={item.id}
              imageUrl={item.bilde_url}
              emoji={item.emoji}
              name={item.navn}
              onClick={() => openDetail(item, 'mine')}
              onExpand={item.bilde_url ? () => setLightboxUrl(item.bilde_url) : undefined}
              statusColor={statusColor}
              statusBg={statusBg}
              statusLabel={statusLabel}
              StatusIcon={StatusIcon}
              ActionIcon={item.status === 'available' ? ArrowRight : undefined}
              onAction={item.status === 'available' ? () => setLoanItemId(item.id) : undefined}
              actionLabel={item.status === 'available' ? 'Lån ut' : undefined}
              topLeft={item.status === 'loaned' && loanedTo && (
                <ImageTileBadge>
                  <Avatar name={loanedTo.full_name} color={loanedTo.avatar_color} size="sm" />
                  {loanedTo.full_name?.split(' ')[0]}
                </ImageTileBadge>
              )}
              orgBadge={item.organization ? { name: item.organization.name } : undefined}
            />
          );
        })}
      </div>
      </>}

      {/* ── Jeg har lånt ── */}
      {activeTab === 'loans' && <>
        {myLoans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Info size={32} color="var(--text3)" strokeWidth={1.8} />
            </div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Du låner ingenting akkurat nå</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {myLoans.map(item => {
              const owner = item.owner;
              const overdue = isOverdue(item.due_date, item.status);
              const isPending = item.status === 'pending';
              const dueDateLabel = item.due_date ? new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : null;

              let statusColor = 'var(--blue)';
              let statusBg = 'var(--blue-light)';
              let StatusIcon = Package;
              let statusLabel = dueDateLabel ? `Lånt til ${dueDateLabel}` : 'Lånt';
              if (isPending) { statusColor = 'var(--amber)'; statusBg = 'var(--amber-light)'; StatusIcon = Clock; statusLabel = 'Venter'; }
              else if (overdue) { statusColor = 'var(--red)'; statusBg = 'var(--red-light)'; StatusIcon = AlertTriangle; statusLabel = dueDateLabel ? `Forfalt ${dueDateLabel}` : 'Forfalt'; }

              return (
                <ImageTile
                  key={item.id}
                  imageUrl={item.bilde_url}
                  emoji={item.emoji}
                  name={item.navn}
                  onClick={() => openDetail(item as InventoryItem, 'loans')}
                  onExpand={item.bilde_url ? () => setLightboxUrl(item.bilde_url) : undefined}
                  statusColor={statusColor}
                  statusBg={statusBg}
                  statusLabel={statusLabel}
                  StatusIcon={StatusIcon}
                  topLeft={owner && (
                    <ImageTileBadge>
                      <Avatar name={owner.full_name} color={owner.avatar_color} size="sm" />
                      {owner.full_name?.split(' ')[0]}
                    </ImageTileBadge>
                  )}
                  orgBadge={item.organization ? { name: item.organization.name } : undefined}
                />
              );
            })}
          </div>
        )}
      </>}

      {/* ── Utlånt av meg ── */}
      {activeTab === 'lentout' && <>
        {myLentOut.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text2)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Info size={32} color="var(--text3)" strokeWidth={1.8} />
            </div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Du har ikke lånt ut noe akkurat nå</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {myLentOut.map(item => {
              const borrower = item.loaned_to;
              const overdue = isOverdue(item.due_date, item.status);
              const dueDateLabel = item.due_date ? new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : null;

              return (
                <ImageTile
                  key={item.id}
                  imageUrl={item.bilde_url}
                  emoji={item.emoji}
                  name={item.navn}
                  onClick={() => openDetail(item as InventoryItem, 'lentout')}
                  onExpand={item.bilde_url ? () => setLightboxUrl(item.bilde_url) : undefined}
                  statusColor={overdue ? 'var(--red)' : 'var(--amber)'}
                  statusBg={overdue ? 'var(--red-light)' : 'var(--amber-light)'}
                  statusLabel={overdue ? (dueDateLabel ? `Forfalt ${dueDateLabel}` : 'Forfalt') : (dueDateLabel ? `Utlånt til ${dueDateLabel}` : 'Utlånt')}
                  StatusIcon={overdue ? AlertTriangle : Package}
                  topLeft={borrower && (
                    <ImageTileBadge>
                      <Avatar name={borrower.full_name} color={borrower.avatar_color} size="sm" />
                      {borrower.full_name?.split(' ')[0]}
                    </ImageTileBadge>
                  )}
                  orgBadge={item.organization ? { name: item.organization.name } : undefined}
                />
              );
            })}
          </div>
        )}
      </>}

      {/* ── Detaljbunnark ── */}
      {detailItem && detailSection && (() => {
        const item = detailItem;
        const overdue = isOverdue(item.due_date, item.status);

        if (detailSection === 'mine') {
          const loanedTo = item.loaned_to;
          return (
            <BottomSheet title={item.navn} onClose={closeDetail}>
              {item.bilde_url
                ? <div style={{ position: 'relative', marginBottom: 16 }} onClick={() => setLightboxUrl(item.bilde_url)}>
                    <img src={item.bilde_url} alt={item.navn}
                      style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block', cursor: 'zoom-in' }} />
                    <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '4px 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Maximize2 size={14} color="#fff" strokeWidth={2} />
                    </div>
                  </div>
                : item.emoji && (
                    <div style={{ width: '100%', height: 110, borderRadius: 'var(--radius)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                      <CategoryIcon emoji={item.emoji} size={52} color="var(--green)" />
                    </div>
                  )
              }
              {item.beskrivelse && <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.6 }}>{item.beskrivelse}</p>}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {item.status === 'available' && <Badge variant="green">Tilgjengelig</Badge>}
                {item.status === 'pending' && loanedTo && <Badge variant="amber">⏳ {loanedTo.full_name} ber om å låne</Badge>}
                {item.status === 'loaned' && loanedTo && <Badge variant={overdue ? 'red' : 'amber'}>{overdue ? '⚠ Forfalt' : `Utlånt til ${loanedTo.full_name}`}</Badge>}
              </div>

              {item.due_date && item.status === 'loaned' && (
                <div style={{ fontSize: 13, color: overdue ? 'var(--red)' : 'var(--text3)', padding: '8px 12px', background: overdue ? '#fee2e2' : 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
                  {overdue ? '⚠ Forfalt: ' : '📅 Leveres: '}{new Date(item.due_date).toLocaleDateString('nb-NO')}
                </div>
              )}

              {item.status === 'available' && item.return_message && (
                <div style={{ fontSize: 13, color: 'var(--green-dark)', padding: '8px 12px', background: 'var(--green-light)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
                  💬 {item.return_message}
                </div>
              )}

              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 16px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {item.status === 'available' && (
                  <button onClick={() => { closeDetail(); setLoanItemId(item.id); }}
                    style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <ArrowRight size={16} strokeWidth={2} /> Lån ut
                  </button>
                )}
                {item.status === 'pending' && (
                  <>
                    <textarea
                      value={approveMessage}
                      onChange={e => setApproveMessage(e.target.value)}
                      placeholder="Legg til en melding til låntaker (valgfritt), f.eks. «Setter den bak garasjen»"
                      rows={2}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13.5, fontFamily: 'inherit', resize: 'none', marginBottom: 10, boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={async () => { await approveLoanItem(item.id, approveMessage); setApproveMessage(''); closeDetail(); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        <Check size={14} strokeWidth={2.5} style={{ display: 'inline', marginRight: 4 }} />Godkjenn
                      </button>
                      <button onClick={async () => { await rejectLoanItem(item.id); setApproveMessage(''); closeDetail(); }} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        <X size={14} strokeWidth={2.5} style={{ display: 'inline', marginRight: 4 }} />Avvis
                      </button>
                    </div>
                  </>
                )}
                {item.status === 'loaned' && (
                  <button onClick={async () => { await returnItem(item.id); closeDetail(); }} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                    ✓ Merk som tilbake
                  </button>
                )}
                <button onClick={() => { closeDetail(); setEditItemId(item.id); }}
                  style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Pencil size={14} strokeWidth={2} /> Rediger
                </button>
                <button onClick={async () => { closeDetail(); await handleDelete(item.id); }}
                  style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Trash2 size={14} strokeWidth={2} /> Slett
                </button>
              </div>
            </BottomSheet>
          );
        }

        if (detailSection === 'loans') {
          const owner = item.owner;
          const isPending = item.status === 'pending';
          const groupNames = getGroupNames(item);
          return (
            <BottomSheet title={item.navn} onClose={closeDetail}>
              {item.bilde_url
                ? <div style={{ position: 'relative', marginBottom: 16 }} onClick={() => setLightboxUrl(item.bilde_url)}>
                    <img src={item.bilde_url} alt={item.navn}
                      style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block', cursor: 'zoom-in' }} />
                    <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '4px 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Maximize2 size={14} color="#fff" strokeWidth={2} />
                    </div>
                  </div>
                : item.emoji && (
                    <div style={{ width: '100%', height: 110, borderRadius: 'var(--radius)', background: isPending ? 'var(--amber-light)' : 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                      <CategoryIcon emoji={item.emoji} size={52} color={isPending ? 'var(--amber)' : 'var(--green)'} />
                    </div>
                  )
              }

              {/* Status */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {isPending
                  ? <Badge variant="amber">⏳ Venter på godkjenning</Badge>
                  : <Badge variant="blue">✓ Du låner</Badge>
                }
                {!isPending && overdue && <Badge variant="red">⚠ Forfalt</Badge>}
              </div>

              {/* Leveringsdato */}
              {item.due_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: overdue ? 'var(--red-light)' : 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
                  <CalendarDays size={16} color={overdue ? 'var(--red)' : 'var(--text3)'} strokeWidth={2} />
                  <div>
                    <div style={{ fontSize: 11, color: overdue ? 'var(--red)' : 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {overdue ? 'Forfalt' : 'Leveres'}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: overdue ? 'var(--red)' : 'var(--text)' }}>
                      {new Date(item.due_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              )}

              {/* Eier */}
              {(owner || item.organization) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
                  {item.organization ? (
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Building2 size={17} color="var(--green-dark)" strokeWidth={1.8} />
                    </div>
                  ) : (
                    <Avatar name={owner!.full_name} color={owner!.avatar_color} size="md" />
                  )}
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Eier</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{item.organization ? item.organization.name : owner!.full_name}</div>
                  </div>
                </div>
              )}

              {/* Beskrivelse */}
              {item.beskrivelse && (
                <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.65, marginBottom: 14 }}>{item.beskrivelse}</p>
              )}

              {/* Antall + grupper */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                <Badge variant="blue">{item.antall} {item.enhet}</Badge>
                {groupNames.map(n => <Badge key={n} variant="blue">👥 {n}</Badge>)}
              </div>

              {!isPending && item.approval_message && (
                <div style={{ fontSize: 13, color: 'var(--green-dark)', padding: '8px 12px', background: 'var(--green-light)', borderRadius: 'var(--radius-sm)', marginBottom: 16 }}>
                  💬 {item.approval_message}
                </div>
              )}

              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 16px' }} />

              {isPending
                ? <button onClick={async () => { await cancelLoanItem(item.id); closeDetail(); }} style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <X size={14} strokeWidth={2.5} /> Avbryt forespørsel
                  </button>
                : <>
                    <textarea
                      value={returnMessage}
                      onChange={e => setReturnMessage(e.target.value)}
                      placeholder="Legg til en melding til eieren (valgfritt), f.eks. «Satte den bak huset under presenningen»"
                      rows={2}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13.5, fontFamily: 'inherit', resize: 'none', marginBottom: 10, boxSizing: 'border-box' }}
                    />
                    <button onClick={async () => { await returnItem(item.id, returnMessage); setReturnMessage(''); fetchMyLoans(user!.id); closeDetail(); }} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                      ✓ Lever tilbake
                    </button>
                  </>
              }
            </BottomSheet>
          );
        }

        if (detailSection === 'lentout') {
          const borrower = item.loaned_to;
          const groupNames = getGroupNames(item);
          return (
            <BottomSheet title={item.navn} onClose={closeDetail}>
              {item.bilde_url
                ? <div style={{ position: 'relative', marginBottom: 16 }} onClick={() => setLightboxUrl(item.bilde_url)}>
                    <img src={item.bilde_url} alt={item.navn}
                      style={{ width: '100%', height: 190, objectFit: 'cover', borderRadius: 'var(--radius)', display: 'block', cursor: 'zoom-in' }} />
                    <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '4px 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Maximize2 size={14} color="#fff" strokeWidth={2} />
                    </div>
                  </div>
                : item.emoji && (
                    <div style={{ width: '100%', height: 110, borderRadius: 'var(--radius)', background: overdue ? '#fef3c7' : 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                      <CategoryIcon emoji={item.emoji} size={52} color={overdue ? '#92400e' : 'var(--text3)'} />
                    </div>
                  )
              }
              {borrower && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <Avatar name={borrower.full_name} color={borrower.avatar_color} size="md" />
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>Låner</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{borrower.full_name}</div>
                  </div>
                </div>
              )}
              {groupNames.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {groupNames.map(n => <Badge key={n} variant="blue">👥 {n}</Badge>)}
                </div>
              )}
              {item.due_date && (
                <div style={{ fontSize: 13, color: overdue ? 'var(--red)' : 'var(--text3)', padding: '8px 12px', background: overdue ? '#fee2e2' : 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
                  {overdue ? '⚠ Forfalt: ' : '📅 Leveres: '}{new Date(item.due_date).toLocaleDateString('nb-NO')}
                </div>
              )}
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 16px' }} />
              <button onClick={async () => { await returnItem(item.id); fetchMyLentOut(user!.id); closeDetail(); }} style={{ width: '100%', padding: 13, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                ✓ Merk som tilbake
              </button>
            </BottomSheet>
          );
        }

        return null;
      })()}

      {editItemId && items.find(i => i.id === editItemId) && (
        <EditItemSheet item={items.find(i => i.id === editItemId)!} onClose={() => setEditItemId(null)} />
      )}

      {addOpen && (
        <BottomSheet title="Legg til lånbare ting" onClose={onCloseAdd}>
          <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Gå til «Inventar»-fanen</div>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>
              Legg til tingen i Inventar og slå på «Kan lånes ut» — da vises den her og i Oversikt for gruppen din.
            </p>
          </div>
          <SecondaryBtn onClick={onCloseAdd}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {loanItemId && <LoanOutSheet presetItemId={loanItemId} onClose={() => setLoanItemId(null)} />}

      {dialog}
      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
};
